<template>
  <!-- Data Inspection Modal -->
  <div v-if="show" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="header-left">
          <h3>Blob Data Inspection</h3>
          <div class="search-container">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search in blob data..."
              class="search-input"
              @input="performSearch"
            />
            <div v-if="searchResults.length > 0" class="search-results">
              {{ currentSearchIndex + 1 }} / {{ searchResults.length }}
              <button
                :disabled="searchResults.length === 0"
                class="search-nav"
                @click="previousSearch"
              >
                ↑
              </button>
              <button :disabled="searchResults.length === 0" class="search-nav" @click="nextSearch">
                ↓
              </button>
            </div>
          </div>
        </div>
        <button class="modal-close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <div v-if="blobError" class="cache-message cache-error">
          {{ blobError }}
        </div>
        <div v-else-if="loading" class="loading-message">Loading blob data...</div>
        <div v-else-if="formattedData" class="blob-content">
          <div class="blob-controls">
            <div class="data-stats">
              {{ formattedData.split('\n').length.toLocaleString() }} lines |
              {{ Math.round(formattedData.length / 1024).toLocaleString() }}KB
            </div>
          </div>
          <div ref="virtualContainer" class="virtual-container" @scroll="onScroll">
            <div class="virtual-spacer" :style="{ height: spacerHeight + 'px' }"></div>
            <div class="virtual-content">
              <div
                v-for="(line, index) in visibleLines"
                :key="startIndex + index"
                :class="{ 'highlighted-line': isLineHighlighted(startIndex + index) }"
                class="code-line"
              >
                <span class="line-number">{{ startIndex + index + 1 }}</span>
                <span class="line-content" v-html="highlightSearchTerms(line)"></span>
              </div>
            </div>
            <div class="virtual-spacer" :style="{ height: endSpacerHeight + 'px' }"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { client, parseErrorMessage } from '../client.js';

interface Props {
  show: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

// Basic state
const loading = ref(false);
const blobData = ref<any>(null);
const blobError = ref<string | null>(null);

// View and search state
const searchQuery = ref('');
const searchResults = ref<number[]>([]);
const currentSearchIndex = ref(0);

// Virtualization state
const virtualContainer = ref<HTMLElement | null>(null);
const lineHeight = 20; // pixels per line
const containerHeight = ref(600);
const scrollTop = ref(0);
const visibleLineCount = computed(() => Math.ceil(containerHeight.value / lineHeight) + 10);
const startIndex = computed(() => Math.max(0, Math.floor(scrollTop.value / lineHeight) - 5));
const endIndex = computed(() =>
  Math.min(allLines.value.length, startIndex.value + visibleLineCount.value),
);

// Data processing
const formattedData = computed(() => {
  if (!blobData.value) return '';

  try {
    return JSON.stringify(blobData.value, null, 2);
  } catch (err) {
    return String(blobData.value);
  }
});

const allLines = computed(() => {
  return formattedData.value.split('\n');
});

const visibleLines = computed(() => {
  return allLines.value.slice(startIndex.value, endIndex.value);
});

const spacerHeight = computed(() => startIndex.value * lineHeight);
const endSpacerHeight = computed(() => (allLines.value.length - endIndex.value) * lineHeight);

// Search functionality
const performSearch = () => {
  searchResults.value = [];
  currentSearchIndex.value = 0;

  if (!searchQuery.value.trim()) return;

  const query = searchQuery.value.toLowerCase();
  allLines.value.forEach((line, index) => {
    if (line.toLowerCase().includes(query)) {
      searchResults.value.push(index);
    }
  });

  if (searchResults.value.length > 0) {
    scrollToSearchResult(0);
  }
};

const nextSearch = () => {
  if (searchResults.value.length === 0) return;
  currentSearchIndex.value = (currentSearchIndex.value + 1) % searchResults.value.length;
  scrollToSearchResult(currentSearchIndex.value);
};

const previousSearch = () => {
  if (searchResults.value.length === 0) return;
  currentSearchIndex.value =
    currentSearchIndex.value === 0 ? searchResults.value.length - 1 : currentSearchIndex.value - 1;
  scrollToSearchResult(currentSearchIndex.value);
};

const scrollToSearchResult = (index: number) => {
  if (!virtualContainer.value) return;

  const lineIndex = searchResults.value[index];
  const targetScrollTop = lineIndex * lineHeight;
  virtualContainer.value.scrollTop = targetScrollTop;
};

const isLineHighlighted = (lineIndex: number): boolean => {
  if (searchResults.value.length === 0) return false;
  const currentResultLineIndex = searchResults.value[currentSearchIndex.value];
  return lineIndex === currentResultLineIndex;
};

const highlightSearchTerms = (line: string): string => {
  if (!searchQuery.value.trim()) return escapeHtml(line);

  const query = searchQuery.value.toLowerCase();
  const escapedLine = escapeHtml(line);
  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');

  return escapedLine.replace(regex, '<mark>$1</mark>');
};

const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

const escapeRegex = (text: string): string => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Scroll handling
const onScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  scrollTop.value = target.scrollTop;
};

// Container resize handling
const updateContainerHeight = () => {
  if (virtualContainer.value) {
    containerHeight.value = virtualContainer.value.clientHeight;
  }
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (virtualContainer.value) {
    resizeObserver = new ResizeObserver(updateContainerHeight);
    resizeObserver.observe(virtualContainer.value);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

// Data loading
const loadBlobData = async () => {
  if (!client.value) return;

  loading.value = true;
  blobError.value = null;
  blobData.value = null;

  try {
    blobData.value = await client.value.getBlob('main');
  } catch (err: any) {
    blobError.value = `Failed to load blob data: ${parseErrorMessage(err)}`;
    console.error('Error loading blob data:', err);
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  console.log('Closing Blob Inspector modal');
  emit('close');
  // Clear data when closing
  blobData.value = null;
  blobError.value = null;
  searchQuery.value = '';
  searchResults.value = [];
  currentSearchIndex.value = 0;
  scrollTop.value = 0;
};

// Watch for show prop changes to load data when modal opens
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      loadBlobData();
      nextTick(() => {
        updateContainerHeight();
      });
    }
  },
);
</script>

<style scoped>
.cache-message {
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 500;
}

.cache-error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.loading-message {
  padding: 1rem;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 95vw;
  max-height: 95vh;
  width: 1400px;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-input {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 250px;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-results {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
}

.search-nav {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
}

.search-nav:hover:not(:disabled) {
  background: #e5e7eb;
}

.search-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.blob-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.blob-controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.data-stats {
  font-size: 0.75rem;
  color: #6b7280;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.virtual-container {
  flex: 1;
  overflow: auto;
  position: relative;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.75rem;
  line-height: 1.25;
  background: #fafafa;
}

.virtual-spacer {
  width: 100%;
}

.virtual-content {
  position: relative;
}

.code-line {
  display: flex;
  min-height: 20px;
  line-height: 20px;
  padding: 0 1rem;
  border-bottom: 1px solid transparent;
  white-space: pre;
}

.code-line:hover {
  background: rgba(59, 130, 246, 0.05);
}

.highlighted-line {
  background: rgba(255, 235, 59, 0.3) !important;
  border-bottom-color: rgba(255, 235, 59, 0.5) !important;
}

.line-number {
  min-width: 3rem;
  text-align: right;
  color: #9ca3af;
  margin-right: 1rem;
  user-select: none;
  border-right: 1px solid #e5e7eb;
  padding-right: 0.5rem;
}

.line-content {
  flex: 1;
  color: #1f2937;
  word-break: break-all;
}

/* Search highlighting */
:deep(mark) {
  background: #fde047;
  color: #374151;
  padding: 0;
  border-radius: 2px;
}

/* Responsive modal */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-content {
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .modal-header {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }

  .header-left {
    flex: 1;
    gap: 0.75rem;
    margin-right: 1rem;
  }

  .search-container {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .search-input {
    width: 100%;
  }

  .modal-close {
    flex-shrink: 0;
    align-self: flex-start;
  }

  .virtual-container {
    font-size: 0.6875rem;
  }

  .line-number {
    min-width: 2.5rem;
    margin-right: 0.5rem;
  }
}
</style>
